import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI, handleAuthSuccess, patientAPI } from '../services/api';
import { tokenManager } from '../services/tokenManager';
import { paymentUtils } from '../utils/paymentUtils';
import { useLocation } from "react-router-dom";

import { reportAdsConversion } from '../utils/reportAdsConversion';


const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentData, setPaymentData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [buttonLoading, setButtonLoading] = useState(false);



  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setError('No session ID found in URL');
      setLoading(false);
      return;
    }

    const processPaymentSuccess = async () => {
      try {
        console.log('Processing payment success with session ID:', sessionId);
        const response = await authAPI.getPaymentSuccessData(sessionId);
        console.log('Payment success response:', response);
        tokenManager.setTokens(response.access_token);
        tokenManager.setUser(response.user);
        setUserData(response.user);
        setPaymentData(response.payment);
        // Set payment completed flag
        paymentUtils.setPaymentCompleted();
      } catch (error) {
        console.error('❌ Payment success processing failed:', error);
        console.error('Error response:', error.response?.data);
        setError(error.response?.data?.error || 'Failed to process payment success');
      } finally {
        setLoading(false);
      }
    };

    processPaymentSuccess();



    //report payment to google tag
    reportAdsConversion(paymentData)



  }, [searchParams]);


  const handleNext = async () => {
    try {
      setButtonLoading(true);
      const profileResponse = await patientAPI.getMyProfile();
      
      if (profileResponse && profileResponse.patientProfile.care_plan_onboarding_completed) {
        navigate('/nurses/list');
      } else {
        navigate('/diagnosis');
      }
    } catch (error) {
      console.error('Error checking patient profile:', error);
      // If there's an error, default to diagnosis page
      navigate('/diagnosis');
    }
  };

  const formatAmount = (amountCents, currency = 'AUD') => {
    if (!amountCents) return 'N/A';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amountCents / 100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Processing your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition duration-150 ease-in-out"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (


    <div className="min-h-screen bg-white flex items-center justify-center py-6 px-4">
      <div className="max-w-md w-full mx-auto">
        {/* ✅ Success Header */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
            <p className="text-gray-600">Welcome to ADHD Rapid Care, {userData?.name}!</p>
          </div>
        </div>

        {/* ✅ Payment Details */}
        <div className="bg-white rounded-lg shadow-lg p-4 mt-4">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Payment Details</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Amount Paid:</span>
              <span className="font-semibold">{formatAmount(paymentData?.amountCents, paymentData?.currency)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Date:</span>
              <span className="font-semibold">{formatDate(paymentData?.completedAt)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Email:</span>
              <span className="font-semibold">{userData?.email}</span>
            </div>
            {paymentData?.plan && (
              <div className="flex justify-between">
                <span className="text-gray-600">Plan:</span>
                <span className="font-semibold">{paymentData?.plan}</span>
              </div>
            )}
          </div>
        </div>

        {/* ✅ Account Info */}
        <div className="bg-white rounded-lg shadow-lg p-4 mt-4 text-sm text-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Your Account Info</h2>
          <div className="flex justify-between">
            <span>Email:</span>
            <span className="font-semibold">{userData?.email}</span>
          </div>
          <div className="flex justify-between mt-2">
            <span>Temporary Password:</span>
            <span className="font-semibold">adhd2025</span>
          </div>
          <p className="mt-2 text-xs text-gray-500">You can change this later.</p>
        </div>

        {/* ✅ CTA Button */}
        <div className="flex flex-col gap-4 mt-6">
          <button
            onClick={handleNext}
            disabled={buttonLoading}
            className="bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition duration-150 ease-in-out flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {buttonLoading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Please wait...
              </>
            ) : (
              'Start Onboarding Questionnaire'
            )}
          </button>
          <p className="text-center text-xs text-gray-500">
            Start Step 1 and personalize your care
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
